import {Store} from '@ngrx/store';
import {MatIconModule} from '@angular/material/icon';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ChangeDetectionStrategy, Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';

import {Role} from './../../core/model/role';
import {MessageService} from './../../services/message.service';
import {
  CreateNewRoleAction,
  DeleteRoleAction,
  GetAllRolesAction,
  UpdateRoleAction,
} from './../../core/store/roles/roles.action';
import {OphInputOrangeComponent} from './../../shared/design/oph-inputs/oph-input-orange/oph-input-orange.component';
import {OphDialogFooterComponent} from './../../shared/design/oph-dialog-footer/oph-dialog-footer.component';
import {createDuplicateValidator} from './../../shared/utils/duplicate-fc-validator';

export type RoleDialogData = {
  role: Role;
  roles?: Role[];
};

@Component({
  selector: 'role-dialog',
  standalone: true,
  imports: [OphInputOrangeComponent, FormsModule, ReactiveFormsModule, OphDialogFooterComponent, MatIconModule],
  templateUrl: './role-dialog.component.html',
  styleUrl: './role-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoleDialogComponent implements OnInit {
  readonly data = inject<RoleDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<RoleDialogData>);
  private messageService = inject(MessageService);
  private store$ = inject(Store);
  private fb = inject(FormBuilder);

  private readonly duplicateRoleValidator = createDuplicateValidator((value: string) => {
    if (!this.data.roles) return false;
    const displayName = value.toLowerCase();
    return this.data.roles.some(
      role => role.displayName.toLowerCase() === displayName && role.id !== this.data.role?.id
    );
  });

  public roleFg = this.fb.group({
    id: this.data.role?.id || null,
    displayName: [this.data.role?.displayName || '', [Validators.required, this.duplicateRoleValidator]],
  });

  public menuOptions: string[] = ['delete'];

  isLoading = false;
  roleHasId = false;

  ngOnInit(): void {
    this.roleHasId = !!this.data.role?.id;
    this.roleFg = this.fb.group({
      id: this.data.role?.id || null,
      displayName: [this.data.role?.displayName || '', [Validators.required, this.duplicateRoleValidator]],
    });
  }

  footerActionLabels() {
    return {
      cancel: 'Cancel',
      save: this.roleHasId ? 'Update Role' : 'Create Role',
    };
  }

  onSave() {
    if (this.roleHasId) {
      this.updateRole();
      return;
    }
    this.createRole();
  }

  onCancel() {
    this.dialogRef.close();
  }

  onMenuOption(type: (typeof this.menuOptions)[number]) {
    if (type === 'delete') {
      this.deleteRole();
    }
  }

  createRole(): void {
    if (this.roleFg.invalid) {
      this.roleFg.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    this.store$.dispatch(
      new CreateNewRoleAction({
        displayName: this.roleFg.get('displayName')?.value,
        onSuccess: () => {
          this.isLoading = false;
          this.dialogRef.close(this.roleFg.value);
          this.messageService.add(`Role created successfully`, 'newTaskAssigned');
          this.store$.dispatch(new GetAllRolesAction({}));
        },
        onFailure: error => {
          this.isLoading = false;
          this.messageService.add(`Error creating role`, 'error');
          console.error('Error creating role:', error);
        },
      })
    );
  }

  updateRole(): void {
    if (this.roleFg.invalid) {
      this.roleFg.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    this.store$.dispatch(
      new UpdateRoleAction({
        role: {...this.roleFg.value, permissions: this.data.role.permissions} as Role,
        onSuccess: () => {
          this.isLoading = false;
          this.dialogRef.close(this.roleFg.value);
          this.messageService.add(`Role updated successfully`, 'newTaskAssigned');
          this.store$.dispatch(new GetAllRolesAction({}));
        },
        onFailure: error => {
          this.isLoading = false;
          this.messageService.add(`Error creating role`, 'error');
          console.error('Error creating role:', error);
        },
      })
    );
  }

  deleteRole() {
    this.isLoading = true;

    const {displayName} = this.data.role;

    this.store$.dispatch(
      new DeleteRoleAction({
        roleId: this.data.role.id,
        onSuccess: () => {
          this.store$.dispatch(new GetAllRolesAction({}));
          this.messageService.add(`Role "${displayName}" deleted successfully`, 'taskCompleted');
          this.isLoading = false;
          this.dialogRef.close();
        },
        onFailure: (error: Error) => {
          this.messageService.add(`Error deleting role "${displayName}": ${error.message}`, 'error');
        },
      })
    );
  }
}
